import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * ## Why disabled is not `opacity-50`
 *
 * It was, and it produced a button measuring **2.13:1 in light and 2.06:1 in
 * dark** — roughly half the readable threshold. Opacity fades the text and the
 * background *together*, so the gap between them collapses at the same rate as
 * the ink: the result is not "quieter", it is illegible, and no contrast check
 * catches it because the tokens themselves are all fine.
 *
 * Disabled is now painted with tokens instead — `muted` behind
 * `muted-foreground`, a pair the contrast script measures — so it reads as
 * inert without becoming unreadable. `ghost` and `link` take only the text
 * colour: a grey fill behind a link is a box that was never there.
 */
const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:text-muted-foreground [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-primary text-primary-foreground shadow-button hover:bg-primary/90 disabled:bg-muted disabled:shadow-none",
        destructive:
          "bg-destructive text-destructive-foreground shadow-button hover:bg-destructive/90 disabled:bg-muted disabled:shadow-none",
        outline:
          "border border-input bg-card hover:bg-secondary hover:text-secondary-foreground disabled:border-border disabled:bg-muted",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80 disabled:bg-muted",
        ghost: "hover:bg-secondary hover:text-secondary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // Sized for a mouse — 36px is a form or header action, 32px a toolbar,
        // table or pagination control. A finger gets 44px from the `touch:`
        // variant instead (see tailwind.config.ts), so a dense desktop and a
        // phone are both served without either being the compromise.
        default: "h-9 px-3.5 touch:h-11",
        sm: "h-8 px-3 touch:h-11",
        lg: "h-10 px-4 touch:h-11",
        icon: "size-9 touch:size-11",
        "icon-sm": "size-8 touch:size-11",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** Shows a spinner and disables the button. The skill requires this on every mutation. */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading = false,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    // Slot renders into its single child, so it can take neither the extra
    // spinner element nor a `disabled` attribute (which is meaningless on an
    // anchor). `loading` is therefore a plain-button-only concern.
    if (asChild) {
      return (
        <Slot
          className={cn(buttonVariants({ variant, size }), className)}
          ref={ref}
          {...props}
        >
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled === true || loading}
        // Tells assistive tech the control is working, not broken.
        aria-busy={loading || undefined}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="animate-spin" aria-hidden="true" />
            {children}
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
